/**
 * @author        calvin@zumper.com (Calvin Yau)
 * @copyright     Copyright (c) 2018, Zumper
 * @description   App Reducer
 */

import {
  APP_MOUNTED,
  HIDE_APP_DOWNLOAD_BAR,
  INITIAL_LOCATION_CHANGED,
  INITIAL_LOCATION_HAS_UTM_PARAMS,
  INIT_LOCAL_STORAGE,
  SET_BOT,
  SET_MOBILE,
  SET_ONLINE,
  SET_SENDING_MESSAGE,
  SET_SENDING_TOUR,
  SET_WARN_OFFLINE,
  SHOW_APP_DOWNLOAD_BAR,
  UPDATE_NOW,
} from '@zcode-ui/web-zumper-global-state'
import { falseReducer, payloadReducer, trueReducer } from '@zcode/reducers'
import type { App } from '@zcode/state-type'
import { combineReducers } from 'redux'
import { handleAction, handleActions } from 'redux-actions'
import type { ActionTypes } from '../types'

export const appReducer = combineReducers<App, ActionTypes>({
  appDownloadBarShowing: handleActions<App['appDownloadBarShowing'], ActionTypes>(
    {
      [HIDE_APP_DOWNLOAD_BAR]: falseReducer,
      [SHOW_APP_DOWNLOAD_BAR]: trueReducer,
    },
    false
  ),
  bot: handleAction<App['bot'], ActionTypes, typeof SET_BOT>(
    SET_BOT,
    (state, action) => {
      const { payload } = action
      return !!payload
    },
    false
  ),
  initialLocationChanged: handleAction<
    App['initialLocationChanged'],
    ActionTypes,
    typeof INITIAL_LOCATION_CHANGED
  >(INITIAL_LOCATION_CHANGED, trueReducer, false),
  initialLocationHasUtmParams: handleAction<
    App['initialLocationHasUtmParams'],
    ActionTypes,
    typeof INITIAL_LOCATION_HAS_UTM_PARAMS
  >(INITIAL_LOCATION_HAS_UTM_PARAMS, trueReducer, false),
  localStorageSynced: handleAction<
    App['localStorageSynced'],
    ActionTypes,
    typeof INIT_LOCAL_STORAGE
  >(INIT_LOCAL_STORAGE, trueReducer, false),
  mobile: handleAction<App['mobile'], ActionTypes, typeof SET_MOBILE>(
    SET_MOBILE,
    (state, action) => {
      const { payload } = action
      return !!payload
    },
    false
  ),
  mounted: handleAction<App['mounted'], ActionTypes, typeof APP_MOUNTED>(
    APP_MOUNTED,
    trueReducer,
    false
  ),
  online: handleAction<App['online'], ActionTypes, typeof SET_ONLINE>(
    SET_ONLINE,
    payloadReducer,
    true
  ),
  now: (state: App['now'] | undefined, action: ActionTypes): App['now'] => {
    if (state === undefined || action.type === UPDATE_NOW) {
      return Date.now()
    }
    return state
  },
  warnOffline: handleAction<App['warnOffline'], ActionTypes, typeof SET_WARN_OFFLINE>(
    SET_WARN_OFFLINE,
    payloadReducer,
    false
  ),
  sendingMessage: handleAction<App['sendingMessage'], ActionTypes, typeof SET_SENDING_MESSAGE>(
    SET_SENDING_MESSAGE,
    payloadReducer,
    false
  ),
  sendingTour: handleAction<App['sendingTour'], ActionTypes, typeof SET_SENDING_TOUR>(
    SET_SENDING_TOUR,
    payloadReducer,
    false
  ),
})
